import React, { useState, useMemo, useEffect } from "react";
import {
  Transporter,
  SubscriptionType,
} from "../interfaces/transporters.interface";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { FileUpload } from "primereact/fileupload";
import { Dropdown } from "primereact/dropdown";
import countryList from 'react-select-country-list';

interface TransporterInfoFormProps {
  onSubmit: (data: Partial<Transporter>, logo?: File) => void;
  onUpgradeSubscription: () => void;
  initialData: Partial<Transporter>;
  isLoading: boolean;
  error: string | null;
  getRemainingDriverSlots?: () => Promise<number | null>;
}

export const editableFields: {
  key: keyof Transporter;
  label: string;
  type: "text" | "number" | "country";
}[] = [
  { key: "name", label: "Nom", type: "text" },
  { key: "lastName", label: "Prénom", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "phone", label: "Téléphone", type: "text" },
  { key: "address", label: "Adresse", type: "text" },
  { key: "ZIPCode", label: "Code Postal", type: "number" },
  { key: "country", label: "Pays", type: "country" },
  { key: "companyName", label: "Nom de l'entreprise", type: "text" },
  { key: "SIRETNumber", label: "Numéro SIRET", type: "number" },
];

const TransporterInfoForm: React.FC<TransporterInfoFormProps> = ({
  onSubmit,
  onUpgradeSubscription,
  initialData,
  isLoading,
  error,
  getRemainingDriverSlots,
}) => {
  const [formData, setFormData] = useState<Partial<Transporter>>(initialData);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [remainingDriverSlots, setRemainingDriverSlots] = useState<
    number | null
  >(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(
    initialData.logo || null
  );
  const fileUploadRef = React.useRef<any>(null);

  // Get countries list using react-select-country-list
  const countries = useMemo(() => {
    return countryList().getData().map(country => ({
      code: country.value.toLowerCase(),
      name: country.label
    }));
  }, []);

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  useEffect(() => {
    const fetchRemainingSlots = async () => {
      if (!getRemainingDriverSlots) return;
      try {
        const slots = await getRemainingDriverSlots();
        setRemainingDriverSlots(slots);
      } catch (error) {
        console.error("Failed to fetch remaining driver slots:", error);
      }
    };

    fetchRemainingSlots();
  }, [getRemainingDriverSlots, initialData]);

  useEffect(() => {
    setLogoPreview(initialData.logo || null);
  }, [initialData.logo]);

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | { target: { name: string; value: string | number } }
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const countryOptionTemplate = (option: { code: string; name: string }) => {
    return (
      <div className="flex align-items-center">
        <img
          alt={option.name}
          src={`https://flagcdn.com/24x18/${option.code}.png`}
          className="mr-2"
          style={{ width: '24px', height: '18px' }}
        />
        <div>{option.name}</div>
      </div>
    );
  };

  const selectedCountryTemplate = (option: { code: string; name: string }, props: any) => {
    if (option) {
      return countryOptionTemplate(option);
    }
    return <span>{props.placeholder}</span>;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Create a new object with only the fields we want to update
    const updateData: Partial<Transporter> = {};

    // Only include fields that have values and ensure proper types
    if (formData.name) updateData.name = formData.name;
    if (formData.lastName) updateData.lastName = formData.lastName;
    if (formData.phone) updateData.phone = formData.phone;
    if (formData.address) updateData.address = formData.address;
    if (formData.country) updateData.country = formData.country;
    if (formData.companyName) updateData.companyName = formData.companyName;

    // Handle number fields explicitly
    if (
      formData.ZIPCode !== undefined &&
      formData.ZIPCode !== null &&
      !isNaN(Number(formData.ZIPCode))
    ) {
      updateData.ZIPCode = Number(formData.ZIPCode);
    }
    if (
      formData.SIRETNumber !== undefined &&
      formData.SIRETNumber !== null &&
      !isNaN(Number(formData.SIRETNumber))
    ) {
      updateData.SIRETNumber = Number(formData.SIRETNumber);
    }

    onSubmit(updateData, logoFile || undefined);
  };

  const handleLogoUpload = (event: { files: File[] }) => {
    if (event.files && event.files.length > 0) {
      const file = event.files[0];
      setLogoFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearLogo = () => {
    setLogoFile(null);
    setLogoPreview(initialData.logo || null);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  const logoTemplate = () => {
    if (logoPreview) {
      return (
        <div className="flex flex-col items-center gap-2">
          <img
            src={logoPreview}
            alt="Logo preview"
            className="h-32 w-auto object-contain rounded-xl"
          />
          <Button
            type="button"
            icon="pi pi-times"
            className="p-button-danger p-button-rounded p-button-text"
            onClick={handleClearLogo}
            aria-label="Supprimer le logo"
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-2">
        <i className="pi pi-image text-4xl text-gray-500" />
        <span>Glissez et déposez le logo ici</span>
      </div>
    );
  };

  const getSubscriptionTypeLabel = (type: SubscriptionType | undefined) => {
    switch (type) {
      case SubscriptionType.FREE_TRIAL:
        return "Essai gratuit";
      case SubscriptionType.PAID:
        return "Payant";
      case SubscriptionType.AFFILIATE:
        return "Affilié";
      default:
        return "Inconnu";
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "Non définie";
    return new Date(date).toLocaleDateString();
  };

  const remainingTime = useMemo(() => {
    if (!formData.endDate) return "Non défini";
    const now = new Date();
    const end = new Date(formData.endDate);
    const diffTime = Math.abs(end.getTime() - now.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} jours`;
  }, [formData.endDate]);

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      aria-label="Formulaire d'informations du transporteur"
    >
      <div
        className="bg-gray-100 p-4 rounded-md mb-4"
        role="region"
        aria-label="Informations d'abonnement"
      >
        <h3 className="text-lg font-semibold mb-2 mt-0">
          Informations d'abonnement
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="font-medium">Type d'abonnement: </span>
            <span>{getSubscriptionTypeLabel(formData.subscriptionType)}</span>
          </div>
          {getRemainingDriverSlots && (
            <div className="font-medium">
              <span className="font-medium">Conducteurs restants: </span>
              <span>
                {remainingDriverSlots === null
                  ? "Chargement..."
                  : remainingDriverSlots === -1
                  ? "Illimité"
                  : remainingDriverSlots}
              </span>
            </div>
          )}
          <div>
            <span className="font-medium">Date de début: </span>
            <span>{formatDate(formData.startDate)}</span>
          </div>
          <div>
            <span className="font-medium">Date de fin: </span>
            <span>{formatDate(formData.endDate)}</span>
          </div>
          <div className="col-span-2">
            <span className="font-medium">Temps restant: </span>
            <span>{remainingTime}</span>
          </div>
        </div>
      </div>

      {editableFields.map(({ key, label, type }) => (
        <div key={key}>
          <label
            htmlFor={key}
            className="block text-sm font-medium text-gray-700"
          >
            {label}
          </label>
          {type === "text" ? (
            <InputText
              type="text"
              name={key}
              id={key}
              value={(formData[key] as string) || ""}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-200 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              aria-required="true"
              aria-invalid={!formData[key]}
            />
          ) : type === "number" ? (
            <InputNumber
              name={key}
              id={key}
              value={formData[key] as number}
              onValueChange={handleInputChange}
              className="mt-1 block w-full"
              useGrouping={false}
              aria-required="true"
              aria-invalid={!formData[key]}
              mode="decimal"
              minFractionDigits={0}
              maxFractionDigits={0}
            />
          ) : (
            <Dropdown
              id={key}
              name={key}
              value={countries.find(c => c.name === formData[key]) || null}
              options={countries}
              onChange={(e) => handleInputChange({ target: { name: key, value: e.value.name } })}
              optionLabel="name"
              placeholder="Sélectionnez un pays"
              className="mt-1 block w-full"
              itemTemplate={countryOptionTemplate}
              valueTemplate={selectedCountryTemplate}
              filter
              filterBy="name"
            />
          )}
        </div>
      ))}

      <div>
        <label
          htmlFor="logo"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Logo de l'entreprise (optionnel)
        </label>
        <FileUpload
          ref={fileUploadRef}
          name="logo"
          accept="image/*"
          maxFileSize={1000000}
          onSelect={handleLogoUpload}
          className="mt-1"
          auto
          customUpload
          uploadHandler={(e) => {
            e.options.clear();
            return new Promise((resolve) => resolve(null));
          }}
          emptyTemplate={logoTemplate}
          chooseLabel="Choisir un logo"
          cancelLabel="Annuler"
          chooseOptions={{
            className:
              "p-button-primary font-sans bg-primary py-3 px-6 rounded-xl text-white border-none hover:bg-primary-light transition-all ease-in-out duration-300",
          }}
        />
      </div>

      {error && (
        <Message
          severity="error"
          text={error}
          className="font-sans"
          aria-live="assertive"
        />
      )}
      <div className="flex justify-end">
        <Button
          type="button"
          label="Mettre à niveau l'abonnement"
          className="p-button-secondary font-sans bg-secondary py-3 px-6 rounded-xl text-white border-none hover:bg-primary-light transition-all ease-in-out duration-300"
          onClick={() => setShowUpgradeConfirm(true)}
          disabled={
            isLoading || formData.subscriptionType === SubscriptionType.PAID
          }
          aria-disabled={
            isLoading || formData.subscriptionType === SubscriptionType.PAID
          }
          hidden
        />
        <Button
          type="submit"
          label="Mettre à jour les informations"
          className="p-button-primary font-sans bg-primary py-3 px-6 rounded-xl text-white border-none hover:bg-primary-light transition-all ease-in-out duration-300"
          disabled={isLoading}
          aria-disabled={isLoading}
        >
          {isLoading && (
            <i className="pi pi-spin pi-spinner ms-2" aria-hidden="true" />
          )}
        </Button>
      </div>

      <ConfirmDialog
        visible={showUpgradeConfirm}
        onHide={() => setShowUpgradeConfirm(false)}
        message="Êtes-vous sûr de vouloir mettre à niveau votre abonnement ? Cette action est irréversible."
        header="Confirmation de mise à niveau"
        icon="pi pi-exclamation-triangle"
        accept={onUpgradeSubscription}
        reject={() => setShowUpgradeConfirm(false)}
        aria-label="Dialogue de confirmation de mise à niveau"
      />

      {isLoading && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="alert"
          aria-busy="true"
        >
          <ProgressSpinner aria-label="Chargement en cours" />
        </div>
      )}
    </form>
  );
};

export default TransporterInfoForm;
